<template>
  <div @click="toggle">
    <div class="col px-0 flex-fill mb-2 hold-text-width">
      <div
        class="icon-container mx-auto d-flex align-items-center justify-content-center"
      >
        <!-- SVG Image Mode -->
        <img
          v-if="isSvgPath"
          :src="value ? toggleIcon : defaultIcon"
          :alt="value ? toggleTitle : defaultTitle"
          class="icon-image"
          :class="{
            'icon-on': value,
            'icon-off': !value,
            'icon-disable': disable,
          }"
          @error="handleImageError"
          loading="eager"
        />
        <!-- Material Symbols Mode (fallback) -->
        <span
          v-else
          class="material-symbols-outlined"
          :class="{
            'icon-on': value,
            'icon-off': !value,
            'icon-disable': disable,
          }"
        >
          {{ value ? toggleIcon : defaultIcon }}
        </span>
      </div>
    </div>
    <p class="fw-normal mb-2 text-center" :class="{ 'text-disable': disable }">
      {{ value ? toggleTitle : defaultTitle }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface Props {
  value?: boolean;
  disable?: boolean;
  defaultIcon: string;
  toggleIcon: string;
  defaultTitle: string;
  toggleTitle: string;
}

const props = withDefaults(defineProps<Props>(), {
  value: false,
  disable: false,
});

const emit = defineEmits<{
  (e: "input", value: boolean): void;
}>();

// Detect if the icon is an SVG path or a Material Symbol name
const isSvgPath = computed(() => {
  return props.defaultIcon.includes(".svg") || props.defaultIcon.includes("/");
});

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  console.error("Failed to load image:", img.src);
  console.error("defaultIcon:", props.defaultIcon);
  console.error("toggleIcon:", props.toggleIcon);
};

const toggle = () => {
  if (props.disable) {
    return;
  }
  emit("input", !props.value);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.center-image {
  margin-left: 50%;
}

.hold-text-width {
  min-width: 88px;
}

.icon-container {
  width: 64px;
  height: 64px;
  border-radius: 64em;
  border: #f2f2f2 solid 1px;
  background: #fbfbfb;
}

.icon-on {
  margin: 1.14rem;
  color: #2c7040;
}

.icon-off {
  margin: 1.14rem;
  color: #666666;
}

.icon-disable {
  margin: 1.14rem;
  color: #cccbcb;
}

.text-disable {
  color: #cccbcb;
}

.material-symbols-outlined {
  font-variation-settings: "FILL" 1;
}

.icon-image {
  width: 30px;
  height: 30px;
  object-fit: contain;
  transition: filter 0.2s ease;
}

.icon-image.icon-on {
  filter: brightness(0) saturate(100%) invert(29%) sepia(23%) saturate(1439%)
    hue-rotate(102deg) brightness(95%) contrast(92%);
  /* Color: #2c7040 */
}

.icon-image.icon-off {
  filter: brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%)
    hue-rotate(173deg) brightness(95%) contrast(89%);
  /* Color: #666666 */
}

.icon-image.icon-disable {
  filter: brightness(0) saturate(100%) invert(84%) sepia(0%) saturate(592%)
    hue-rotate(167deg) brightness(94%) contrast(88%);
  /* Color: #cccbcb */
  opacity: 0.6;
}
</style>
